// src/pages/customer/ItemDetailPage/ItemDetailPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import "./ItemDetailPage.css";

// Hooks
import useIsMobile from "../../../hooks/useIsMobile";
import { useUser } from "../../../context/UserContext";
import useProductVariants from "./hooks/useProductVariants";
import { useStoreSettings } from "../../../hooks/useStoreSettings";
import useCurrency from "../../../hooks/useCurrency";
import useMultilingual from "../../../hooks/useMultilingual";
import { useLanguage } from "../../../context/LanguageContext";

// Utils
import { getLocalizedText, getLocalizedProductName } from "../../../utils/multilingualUtils";

// Firebase
import { db, DEFAULT_STORE_ID } from "../../../firebase/firebase";
import { getSalesByProductId } from "../../../firebase/sales-functions";

// Context
import { useCart } from "../../../context/CartContext";

// Utils
import {
  parseProductSlug,
  parseVariantFromUrl,
  generateProductUrl,
  updateUrl,
  generatePageTitle
} from "../../../utils/urlUtils";

// Components
import ImageViewer from "./components/ImageViewer";
import ProductImages from "./components/ProductImages";
import VariantSelector from "./components/VariantSelector";
import StockStatus from "./components/StockStatus";
import ProductActions from "./components/ProductActions";
import ProductInfo from "./components/ProductInfo";
import SalesDisplay from "./components/SalesDisplay/SalesDisplay";
import WishlistToggle from "../../../components/common/WishlistToggle/WishlistToggle";

const ItemDetail = () => {
  // Router hooks
  const { productSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Context and custom hooks
  const isMobile = useIsMobile();
  const { currentUser } = useUser();
  const { formatDisplay, getCurrency } = useCurrency();
  const multilingual = useMultilingual();
  const { language } = useLanguage();
  const { addToCart } = useCart(); // ✅ Use optimized cart context

  // Translations
  const translations = {
    en: {
      productNotAvailable: "Product Not Available",
      productNotAvailableMessage: "This product is currently not available for viewing. It may be in draft mode or temporarily unavailable.",
      productNotFound: "Product Not Found",
      loadingProduct: "Loading product...",
      browseProducts: "Browse Products"
    },
    ar: {
      productNotAvailable: "المنتج غير متاح",
      productNotAvailableMessage: "هذا المنتج غير متاح حاليًا للعرض. قد يكون في وضع المسودة أو غير متاح مؤقتًا.",
      productNotFound: "المنتج غير موجود",
      loadingProduct: "جاري تحميل المنتج...",
      browseProducts: "تصفح المنتجات"
    }
  };

  const t = translations[language] || translations.en;

  // Disable RTL layout for ItemDetail page


  // State
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [variantError, setVariantError] = useState(false);
  const [initialVariantSet, setInitialVariantSet] = useState(false);

  // Sales state
  const [salesData, setSalesData] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);

  // Custom hook for variant management
  const {
    selectedVariant,
    selectedVariantOptions,
    displayImages,
    getVariantTypes,
    getAvailableOptions,
    handleVariantOptionChange,
    setSelectedVariantOptions
  } = useProductVariants(product);

  const storeId = DEFAULT_STORE_ID;
  const { currency: storeCurrency } = useStoreSettings(storeId);

  // Cleanup effect to ensure body overflow is reset when component unmounts
  useEffect(() => {
    return () => {
      // Reset body overflow when component unmounts
      document.body.style.overflow = "";
    };
  }, []);

  // Update page title when product or variant changes
  useEffect(() => {
    if (product) {
      document.title = generatePageTitle(product, selectedVariant, language);
    }
  }, [product, selectedVariant, language]);

  // Update URL when variant changes (after initial load)
  useEffect(() => {
    if (product && selectedVariant && initialVariantSet) {
      const newUrl = generateProductUrl(product, selectedVariant);
      updateUrl(newUrl, true); // Replace current URL
    }
  }, [selectedVariant, product, initialVariantSet]);

  // Update selected image when display images change
  useEffect(() => {
    if (displayImages && displayImages.length > 0) {
      setSelectedImage(displayImages[0]);
      setCurrentImageIndex(0);
    }
  }, [displayImages]);

  // Enhanced product fetching with multiple search methods
  const fetchProductBySlug = async (slug) => {
    const slugInfo = parseProductSlug(slug);
    if (!slugInfo) throw new Error("Invalid product identifier");

    const productsRef = collection(db, "stores", storeId, "products");

    try {
      // 1. If it's an ID, fetch directly (1 Read)
      if (slugInfo.searchBy === 'id') {
        const docRef = doc(db, "stores", storeId, "products", slugInfo.value);
        const productDoc = await getDoc(docRef);

        if (productDoc.exists()) {
          return { id: productDoc.id, ...productDoc.data(), storeId };
        }
      }

      // 2. Query by specific Indexed Fields (1 Read per query)

      // Check SKU
      const skuQuery = query(
        productsRef,
        where("sku", "==", slugInfo.value),
        where("status", "in", ["active", "out of stock"])
      );
      const skuSnapshot = await getDocs(skuQuery);
      if (!skuSnapshot.empty) {
        const doc = skuSnapshot.docs[0];
        return { id: doc.id, ...doc.data(), storeId };
      }

      // Check Slug
      const slugQuery = query(
        productsRef,
        where("slug", "==", slugInfo.value),
        where("status", "in", ["active", "out of stock"])
      );
      const slugSnapshot = await getDocs(slugQuery);
      if (!slugSnapshot.empty) {
        const doc = slugSnapshot.docs[0];
        return { id: doc.id, ...doc.data(), storeId };
      }

      throw new Error("Product not found");

    } catch (error) {
      console.error("❌ Error fetching product:", error);
      throw error;
    }
  };

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      if (!productSlug) {
        setError("Product not found");
        setLoading(false);
        return;
      }

      // Reset all states when productSlug changes
      setLoading(true);
      setError(null);
      setProduct(null);
      setSelectedImage(null);
      setCurrentImageIndex(0);
      setQuantity(1);
      setVariantError(false);
      setInitialVariantSet(false);
      setSalesData([]);

      try {
        // console.log("Fetching product with slug:", productSlug);

        const productData = await fetchProductBySlug(productSlug);
        // console.log("Product data:", productData);

        // Check product status - only allow active and out of stock
        if (productData.status && !['active', 'out of stock'].includes(productData.status)) {
          // console.log("❌ Product status not allowed for public viewing:", productData.status);
          setError("not_available"); // Special error code for unavailable products
          setLoading(false);
          return;
        }

        // Ensure variants have images array initialized
        if (productData.variants) {
          productData.variants = productData.variants.map(variant => ({
            ...variant,
            images: variant.images || []
          }));
        }

        setProduct(productData);

        // Parse variant from URL and set initial selection
        const variantInfo = parseVariantFromUrl(location.search);
        if (variantInfo && productData.variants) {
          if (variantInfo.id) {
            // Find variant by ID
            const foundVariant = productData.variants.find(v => v.id === variantInfo.id);
            if (foundVariant) {
              // Set variant options based on found variant
              const options = {};
              Object.keys(foundVariant).forEach(key => {
                if (!['id', 'price', 'quantity', 'sku', 'images'].includes(key) &&
                  foundVariant[key] !== null && foundVariant[key] !== undefined) {
                  options[key] = foundVariant[key];
                }
              });
              setSelectedVariantOptions(options);
            }
          } else if (variantInfo.options) {
            // Set variant options directly from URL
            setSelectedVariantOptions(variantInfo.options);
          }
        }

        setInitialVariantSet(true);

      } catch (err) {
        console.error("Error fetching product:", err);
        setError("Product not found. Please check the URL and try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productSlug, location.search, storeId]);

  // Fetch sales data when product is loaded
  useEffect(() => {
    const fetchSales = async () => {
      if (!product || !storeId) return;

      setLoadingSales(true);
      try {
        // console.log("🔄 Fetching sales for product:", product.id);
        const sales = await getSalesByProductId(storeId, product.id);
        // console.log("💰 ItemDetail: Fetched sales:", sales);
        setSalesData(sales);
      } catch (error) {
        console.error("❌ Error fetching sales:", error);
      } finally {
        setLoadingSales(false);
      }
    };

    fetchSales();
  }, [product, storeId]);

  // Enhanced variant change handler
  const handleVariantChange = (variantType, value) => {
    handleVariantOptionChange(variantType, value);
  };

  // Get applicable sales for current selection
  const getApplicableSales = () => {
    if (!salesData || salesData.length === 0) return null;

    const productLevelSale = salesData.find(sale => !sale.variantId);
    if (productLevelSale) {
      // console.log("🎯 Using product-level sale:", productLevelSale);
      return productLevelSale;
    }

    if (selectedVariant && selectedVariant.id) {
      const variantSale = salesData.find(sale => sale.variantId === selectedVariant.id);
      if (variantSale) {
        // console.log("🎯 Using variant-specific sale:", variantSale);
        return variantSale;
      }
    }

    return null;
  };

  // Calculate display price with sales
  const getPriceData = () => {
    const applicableSale = getApplicableSales();
    const basePrice = selectedVariant?.price || product?.price || 0;

    if (applicableSale) {
      return {
        originalPrice: basePrice,
        discountedPrice: parseFloat(applicableSale.discountedPrice),
        hasDiscount: true,
        discountLabel: applicableSale.discountType === "percentage"
          ? `${applicableSale.discountValue}% OFF`
          : `${formatDisplay(applicableSale.discountValue)} OFF`,
        isVariantDiscount: !!applicableSale.variantId,
        saleDescription: applicableSale.description
      };
    }

    return {
      originalPrice: basePrice,
      discountedPrice: basePrice,
      hasDiscount: false,
      discountLabel: null,
      isVariantDiscount: false,
      saleDescription: null
    };
  };

  // Image viewer handlers
  const openImageViewer = (index) => {
    setCurrentImageIndex(index);
    setIsViewerOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeImageViewer = () => {
    setIsViewerOpen(false);
    document.body.style.overflow = "";
  };

  const handlePreviousImage = () => {
    const newIndex = (currentImageIndex - 1 + displayImages.length) % displayImages.length;
    setCurrentImageIndex(newIndex);
  };

  const handleNextImage = () => {
    const newIndex = (currentImageIndex + 1) % displayImages.length;
    setCurrentImageIndex(newIndex);
  };

  const swapImage = (index) => {
    setCurrentImageIndex(index);
    setSelectedImage(displayImages[index]);
  };

  // Cart handlers
  const handleAddToCart = async () => {
    if (addingToCart) return;

    const requiredTypes = getVariantTypes();
    const isValid = requiredTypes.every(type => selectedVariantOptions[type]);

    if (!isValid) {
      setVariantError(true);
      return;
    }

    setVariantError(false);
    try {
      setAddingToCart(true);

      const priceData = getPriceData();
      const productForCart = {
        ...product,
        price: priceData.discountedPrice, // ✅ Use 'price' field for consistency
        originalPrice: priceData.originalPrice,
        hasDiscount: priceData.hasDiscount,
        discountInfo: {
          hasDiscount: priceData.hasDiscount,
          isVariantDiscount: priceData.isVariantDiscount,
          discountLabel: priceData.discountLabel,
          saleDescription: priceData.saleDescription
        }
      };

      // ✅ Use optimized addToCart from CartContext (instant UI update)
      await addToCart(productForCart, quantity, selectedVariant);

      // toast.success("Added to cart successfully!");
      // console.log("Added to cart:", result);
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add to cart. Please try again.");
    } finally {
      setAddingToCart(false);
    }
  };

  const handleQuickBuy = async () => {
    try {
      setAddingToCart(true);

      const priceData = getPriceData();
      const productForCart = {
        ...product,
        price: priceData.discountedPrice, // ✅ Use 'price' field for consistency
        originalPrice: priceData.originalPrice,
        hasDiscount: priceData.hasDiscount,
        discountInfo: {
          hasDiscount: priceData.hasDiscount,
          isVariantDiscount: priceData.isVariantDiscount,
          discountLabel: priceData.discountLabel,
          saleDescription: priceData.saleDescription
        }
      };

      // ✅ Use optimized addToCart from CartContext
      await addToCart(productForCart, quantity, selectedVariant);

      navigate("/checkout");
    } catch (error) {
      console.error("Error with quick buy:", error);
      toast.error("Failed to process quick buy. Please try again.");
    } finally {
      setAddingToCart(false);
    }
  };

  // Loading and error states
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>{t.loadingProduct}</p>
      </div>
    );
  }

  // Check for not available error (draft products)
  if (error === "not_available") {
    return (
      <div className="error-container">
        <h2>{t.productNotAvailable}</h2>
        <p>{t.productNotAvailableMessage}</p>
        <button onClick={() => navigate("/shop")}>{t.browseProducts}</button>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="error-container">
        <h2>{error || t.productNotFound}</h2>
        <button onClick={() => navigate("/shop")}>{t.browseProducts}</button>
      </div>
    );
  }

  // Computed values
  const priceData = getPriceData();
  const variantTypes = getVariantTypes();

  // Check out of stock status - handle infinite quantity (-1)
  const isOutOfStock = selectedVariant
    ? (parseInt(selectedVariant.quantity) !== -1 && selectedVariant.quantity <= 0)
    : (parseInt(product.quantity) !== -1 && product.quantity <= 0);
  const availableQuantity = selectedVariant?.quantity ?? product.quantity;

  return (
    <div
      className={`itemD-container ${language === "ar" ? "rtl" : "ltr"}`}
      dir={language === "ar" ? "rtl" : "ltr"}
    >
      <ImageViewer
        isOpen={isViewerOpen}
        images={displayImages}
        currentIndex={currentImageIndex}
        onClose={closeImageViewer}
        onPrevious={handlePreviousImage}
        onNext={handleNextImage}
      />

      <ProductImages
        images={displayImages}
        isMobile={isMobile}
        selectedImage={selectedImage}
        currentImageIndex={currentImageIndex}
        onImageClick={openImageViewer}
        onSwapImage={swapImage}
      />

      <div className="itemD-details">
        <div className="title-and-actions">
          <div className="title-area">
            <div className="itemD-header-row">
              <h1 className="itemD-top-title">{multilingual.getProductName(product)}</h1>
              {/* Sales Display Component */}
              <SalesDisplay
                originalPrice={priceData.originalPrice}
                discountedPrice={priceData.discountedPrice}
                hasDiscount={priceData.hasDiscount}
                discountLabel={priceData.discountLabel}
                isVariantDiscount={priceData.isVariantDiscount}
                saleDescription={priceData.saleDescription}
                selectedVariant={selectedVariant}
                size="large"
                className="itemD-sales-display"
              />

            </div>
          </div>
          <WishlistToggle
            product={product}
            variantId={selectedVariant?.id}
            className="product-wishlist-btn"
          />
        </div>

        <VariantSelector
          product={product}
          variantTypes={variantTypes}
          selectedVariantOptions={selectedVariantOptions}
          onVariantChange={handleVariantChange}
          getAvailableOptions={getAvailableOptions}
          variantError={variantError}
        />

        <StockStatus
          isOutOfStock={isOutOfStock}
          quantity={availableQuantity}
        />

        <ProductActions
          isOutOfStock={isOutOfStock}
          addingToCart={addingToCart}
          onAddToCart={handleAddToCart}
          onQuickBuy={handleQuickBuy}
        />

        <ProductInfo product={product} />
      </div>
    </div>
  );
};

export default ItemDetail;